import { z } from "zod";

import { NumericValueType } from "./numeric-value-type";

/**
 * The AI-written criteria JSON sometimes lands with an OTHER group as a single
 * object instead of an array of groups (seen after the onboarding-test
 * suggestion apply, PREC-3700). Runtime healing happens at the RTKQ boundary in
 * redux/score/normalizeScoreCriteria.ts so consumers always get an array — the
 * OTHER fields below are plain arrays here. (A `z.preprocess(coerceToArray, …)`
 * wrapper was tried but its input type is `unknown`, which breaks the
 * `zodResolver` type used by the ScoreCriteria form; the boundary heal covers
 * the shape without touching the schema's input type.)
 */

/** The criteria JSON of one scoring (rubric): company + persona criterion groups. */
export const ScoreCriteriaCriterionSchema = z.object({
	companyScoring: z.object({
		HQ_COUNTRY: z.object({
			ifNotFound: z.string().optional(),
			open: z.boolean().optional(),
			active: z.boolean().optional(),
			items: z.array(
				z.object({
					active: z.boolean().optional(),
					countries: z.array(z.string()),
					points: z.number(),
					description: z.string(),
				})
			),
		}),
		SUBSIDIARY_COUNTRY: z.object({
			ifNotFound: z.string().optional(),
			open: z.boolean().optional(),
			active: z.boolean().optional(),
			items: z.array(
				z.object({
					active: z.boolean().optional(),
					countries: z.array(z.string()),
					points: z.number(),
					description: z.string(),
				})
			),
		}),
		EMPLOYEE_SIZE: z.object({
			ifNotFound: z.string().optional(),
			open: z.boolean().optional(),
			active: z.boolean().optional(),
			items: z.array(
				z.object({
					lowerEqual: z.number().optional(),
					greaterEqual: z.number().optional(),
					type: z.nativeEnum(NumericValueType).nullable().optional(),
					points: z.number(),
					description: z.string(),
				})
			),
		}),
		INDUSTRY: z.object({
			ifNotFound: z.string().optional(),
			open: z.boolean().optional(),
			active: z.boolean().optional(),
			items: z.array(
				z.object({
					industries: z.array(z.string()).optional(),
					points: z.number(),
					description: z.string(),
				})
			),
		}),
		OTHER: z.array(
			z.object({
				criteriaName: z.string(),
				open: z.boolean().optional(),
				ifNotFound: z.string().optional(),
				active: z.boolean().optional(),
				criteria: z.array(
					z.object({
						points: z.number(),
						type: z.string(),
						description: z.string(),
					})
				),
			})
		),
	}),
	personaScoring: z.object({
		JOB_LEVEL: z.object({
			ifNotFound: z.string().optional(),
			open: z.boolean().optional(),
			active: z.boolean().optional(),
			items: z.array(
				z.object({
					seniorityLevel: z.array(z.string()),
					points: z.number(),
					description: z.string(),
				})
			),
		}),
		PERSON_LOCATION: z.object({
			ifNotFound: z.string().optional(),
			open: z.boolean().optional(),
			active: z.boolean().optional(),
			items: z.array(
				z.object({
					countries: z.array(z.string()).optional(),
					points: z.number(),
					description: z.string(),
				})
			),
		}),
		OTHER: z.array(
			z.object({
				ifNotFound: z.string().optional(),
				open: z.boolean().optional(),
				active: z.boolean().optional(),
				criteriaName: z.string(),
				criteria: z.array(
					z.object({
						points: z.number(),
						type: z.string(),
						description: z.string(),
					})
				),
			})
		),
	}),
});
